import React from 'react';

import {Source} from 'constants/SourceConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import BoxSchemaSelector from 'samples/BoxSchemaSelector';
import * as ModalContainer from 'ui-library/ModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from './panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_BASE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import {getRequiredParentStorableError} from './storables/StorableColumnsUtil';

export interface BulkCreateBoxesFlowProps {
  initialRegistry?: Source | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

class BulkCreateBoxesFlow extends React.Component<BulkCreateBoxesFlowProps> {
  static open(props: Omit<BulkCreateBoxesFlowProps, 'onRequestClose'>): void {
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkCreateBoxesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes: () => Array<Pane> = () => [
    {
      paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
      configuration: StorableImportSettingsModalPane.configure({
        schemaSelectorClass: BoxSchemaSelector,
        schemaIdAccessorKey: 'boxSchemaId',
        showProjectSelector: true,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL() {
          return '/static/img/spreadsheet-import-example-create-boxes.png';
        },
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes() {
          return [
            ...STORABLE_BASE_COLUMN_TYPES,
            COLUMN_TYPE.BOX_SCHEMA_FIELD,
            COLUMN_TYPE.STUDY_LABELS,
            COLUMN_TYPE.IGNORE,
          ];
        },
        getRequiredColumnsError(columns: Array<SpreadsheetColumn>) {
          const requiredParentStorableError = getRequiredParentStorableError(columns);
          if (requiredParentStorableError) {
            return requiredParentStorableError;
          }
          if (columns.some(({type}) => type !== COLUMN_TYPE.IGNORE)) {
            return null;
          }
          return 'At least one column must be specified.';
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateBoxes,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
      }),
    },
  ];

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="box"
        importType={IMPORT_TYPE.BOXES}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.createBoxesFromSpreadsheet}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import boxes"
      />
    );
  }
}

export default BulkCreateBoxesFlow;
